import { AxiosInstance } from '../utilities/requestClient';
import TokenService from '../services/token-service';

import useSnackbar from './useSnackbar';
import { Note } from '../sharedInterfaces/notes';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

type NoteStore = Note[];

export interface UseFundNoteProps {
  fundName: string;
  // the subject is the page, for example "ucits" for ucits Law.
  // Used for filtering when fetching notes (can be undefined to fetch all notes)
  subject?: string;
  // the topic is some additional information for the note. For example, this could be
  //the rule number in ucits law
  // Used for filtering when fetching notes (can be undefined to fetch all notes)
  topic?: string;
  positionDate: string;
  startDate?: string;
  endDate?: string;
  shouldCall: boolean;
  // Subject to use when posting notes (required). If not provided, falls back to subject
  postSubject?: string;
  // Topic to use when posting notes. If not provided, falls back to topic
  postTopic?: string;
  // if you use this, you'll immediately post this as a note before posting your real note
  prependWithDetail?: string;
  otherUrlProvided?: string;
}

export interface UseFundNotesMultipleProps {
  fundNames: string[];
  subject?: string;
  topic?: string;
  date1: string;
  date2?: string;
  shouldCall: boolean;
}

export const getFundNotesData = async (
  fundName: string,
  positionDate: string,
  startDate?: string | null,
  endDate?: string | null,
  subject?: string,
  topic?: string,
): Promise<NoteStore> => {
  const baseUrl = `/get_fund_notes/${fundName}/${startDate || positionDate}/${endDate || positionDate}`;
  const pathSegments = [baseUrl];

  if (subject) pathSegments.push(subject);
  if (topic) pathSegments.push(topic);

  const finalUrl = pathSegments.join('/');

  const response = await AxiosInstance.get(finalUrl);

  // Process timestamps with timezone offset
  const notes = response.data;
  for (let index = 0; index < notes.length; index++) {
    const offset = fundName.includes('adepa') ? 3 : 2;
    const timestamp = new Date(notes[index].fund_note_timestamp);
    timestamp.setHours(timestamp.getHours() + offset);
    notes[index].fund_note_timestamp = timestamp.toISOString();
  }

  return notes;
};

// Response format from POST endpoint: { fund_name: Note[] }
export type FundNotesResponse = Record<string, Note[]>;

export const getFundNotesDataPost = async (
  fundNames: string[],
  date1: string,
  date2?: string,
  subject?: string,
  topic?: string,
): Promise<FundNotesResponse> => {
  // Prepare request body
  const requestBody: {
    funds_names: string[];
    date1: string;
    date2?: string;
    subject?: string;
    topic?: string;
  } = {
    funds_names: fundNames,
    date1,
  };

  if (date2 && date2 !== date1) {
    requestBody.date2 = date2;
  }
  if (subject) {
    requestBody.subject = subject;
  }
  if (topic) {
    requestBody.topic = topic;
  }

  const response = await AxiosInstance.post('/get_fund_notes', requestBody, {
    headers: {
      'content-type': 'application/json',
    },
  });

  // Process timestamps with timezone offset for each fund's notes
  // Response format: { fund_name: Note[] }
  const results: FundNotesResponse = response.data;
  for (const [fundName, notes] of Object.entries(results)) {
    const offset = fundName.includes('adepa') ? 3 : 2;
    for (let index = 0; index < notes.length; index++) {
      const timestamp = new Date(notes[index].fund_note_timestamp);
      timestamp.setHours(timestamp.getHours() + offset);
      notes[index].fund_note_timestamp = timestamp.toISOString();
    }
  }

  return results;
};

const useFundNotes = ({
  fundName,
  subject,
  topic,
  positionDate,
  shouldCall,
  startDate,
  endDate,
  postSubject,
  postTopic,
  ...props
}: UseFundNoteProps) => {
  const { showAsyncSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Fetch notes query
  const {
    data: notes = [],
    refetch: fetchFundNotes,
    isLoading: loading,
    isFetching,
    error,
  } = useQuery({
    queryKey: [
      'fund-notes',
      fundName,
      positionDate,
      startDate,
      endDate,
      subject,
      topic,
    ],
    queryFn: () =>
      getFundNotesData(
        fundName,
        positionDate,
        startDate,
        endDate,
        subject,
        topic,
      ),
    enabled: shouldCall && !!positionDate,
    staleTime: 1000 * 60 * 300, // Optional: cache for 5 minutes
  });



  const handlePost = async (note: Record<string, unknown>) => {
    await AxiosInstance.post('/v1/FundNote', note, {
      headers: {
        'content-type': 'application/json',
      },
    });
  };

  const queryKey = [
    'fund-notes',
    fundName,
    positionDate,
    startDate,
    endDate,
    subject,
    topic,
  ];

  const postMutation = useMutation({
    mutationFn: async (text: string) => {
      // Use postSubject/postTopic for posting, fallback to subject/topic if not provided
      const subjectForPosting = postSubject ?? subject;
      const topicForPosting = postTopic ?? topic;

      if (props.prependWithDetail) {
        const prependNoteData = {
          fund_note_fund_name: fundName,
          fund_note_subject: subjectForPosting,
          fund_note_position_date: positionDate,
          fund_note_topic: topicForPosting,
          fund_note_is_active: true,
          fund_note_text: props.prependWithDetail,
        };
        await handlePost(prependNoteData);
        // Invalidate queries after prepend note is posted
        queryClient.invalidateQueries({
          queryKey: ['fund-notes'],
        });
        queryClient.invalidateQueries({
          queryKey: ['fund-notes-multiple'],
        });
      }
      const noteData = {
        fund_note_fund_name: fundName,
        fund_note_subject: subjectForPosting,
        fund_note_position_date: positionDate,
        fund_note_topic: topicForPosting,
        fund_note_is_active: true,
        fund_note_text: text,
      };
      await showAsyncSnackbar({
        startMessage: 'Saving your note...',
        successMessage: 'Note saved!',
        failureMessage:
          'Failed to save note. Please check yor connection and try again.',
        promiseToResolve: AxiosInstance.post('/v1/FundNote', noteData, {
          headers: {
            'content-type': 'application/json',
          },
        }),
      });
    },
    onMutate: async (text, context) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await context.client.cancelQueries({ queryKey });
      const { user_id, user_name } = TokenService.getUser()!;

      // Snapshot the previous value
      const previousNotes = context.client.getQueryData<Note[]>(queryKey) ?? [];

      // Create the optimistic note
      const subjectForPosting = postSubject ?? subject;
      const topicForPosting = postTopic ?? topic;
      const optimisticNote: Note = {
        fund_note_fund_name: fundName,
        fund_note_subject: (subjectForPosting ??
          '') as Note['fund_note_subject'],
        fund_note_topic: (topicForPosting ?? 'all') as Note['fund_note_topic'],
        fund_note_position_date: positionDate,
        fund_note_text: text,
        fund_note_timestamp: new Date().toISOString(),
        fund_note_user_id: user_id,
        user_name,
      };

      // Optimistically update to the new value
      context.client.setQueryData<Note[]>(queryKey, (old = []) => [
        optimisticNote,
        ...old,
      ]);

      // Also optimistically update the multiple fund notes query if it exists
      // This handles the case where preFetchedNotes is used
      const multipleQueries = context.client.getQueriesData<
        Record<string, Note[]>
      >({
        queryKey: ['fund-notes-multiple'],
      });

      for (const [queryKeyMultiple, data] of multipleQueries) {
        if (data && data[fundName]) {
          context.client.setQueryData<Record<string, Note[]>>(
            queryKeyMultiple,
            (old = {}) => ({
              ...old,
              [fundName]: [optimisticNote, ...(old[fundName] || [])],
            }),
          );
        }
      }

      // Return a context object with the snapshotted value
      return { previousNotes, optimisticNote };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, text, onMutateResult, context) => {
      if (onMutateResult) {
        // Rollback individual query
        context.client.setQueryData(queryKey, onMutateResult.previousNotes);

        // Rollback multiple fund notes queries
        const multipleQueries = context.client.getQueriesData<
          Record<string, Note[]>
        >({
          queryKey: ['fund-notes-multiple'],
        });

        for (const [queryKeyMultiple, data] of multipleQueries) {
          if (data && data[fundName] && onMutateResult.optimisticNote) {
            context.client.setQueryData<Record<string, Note[]>>(
              queryKeyMultiple,
              (old = {}) => {
                const currentNotes = old[fundName] || [];
                const filteredNotes = currentNotes.filter(
                  (note) =>
                    note.fund_note_text !==
                    onMutateResult.optimisticNote.fund_note_text ||
                    note.fund_note_subject !==
                    onMutateResult.optimisticNote.fund_note_subject ||
                    note.fund_note_topic !==
                    onMutateResult.optimisticNote.fund_note_topic ||
                    note.fund_note_position_date !==
                    onMutateResult.optimisticNote.fund_note_position_date,
                );
                return {
                  ...old,
                  [fundName]: filteredNotes,
                };
              },
            );
          }
        }
      }
    },
    // Always refetch after error or success:
    onSettled: (data, error, text, onMutateResult, context) => {
      // Invalidate all fund notes queries to ensure fresh data
      // This includes both single fund notes and multiple fund notes queries
      context.client.invalidateQueries({
        queryKey: ['fund-notes'],
      });
      context.client.invalidateQueries({
        queryKey: ['fund-notes-multiple'],
      });
    },
  });

  return {
    notes,
    fetchFundNotes,
    postFundNotes: postMutation.mutateAsync,
    loading: loading || isFetching,
    isPosting: postMutation.isPending,
  };
};

/**
 * Hook to fetch notes for multiple funds in a single API call
 * Returns notes mapped by fund name for easy access in components
 */
export const useAllFundNotes = ({
  fundNames,
  subject,
  topic,
  date1,
  date2,
  shouldCall,
}: UseFundNotesMultipleProps) => {
  const {
    data: fundNotesData = {},
    refetch: fetchFundNotes,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: [
      'fund-notes-multiple',
      fundNames.sort().join(','), // Sort to ensure consistent cache key
      date1,
      date2,
      subject,
      topic,
    ],
    queryFn: () =>
      getFundNotesDataPost(fundNames, date1, date2, subject, topic),
    enabled: shouldCall && !!date1 && fundNames.length > 0,
    staleTime: 1000 * 60 * 300, // Optional: cache for 5 minutes
  });

  // Create a map of fundName -> notes for easy access
  // Response format is already { fund_name: Note[] }, so we can use it directly
  const notesByFundName = new Map<string, Note[]>();
  Object.entries(fundNotesData).forEach(([fundName, notes]) => {
    notesByFundName.set(fundName, notes);
  });

  // Helper function to get notes for a specific fund
  const getNotesForFund = (fundName: string): Note[] => {
    return notesByFundName.get(fundName) || [];
  };

  return {
    fundNotesData, // Raw dictionary of { fund_name: Note[] }
    notesByFundName, // Map for direct access by fund name
    getNotesForFund, // Helper function to get notes for a specific fund
    fetchFundNotes,
    loading,
    error,
  };
};

export default useFundNotes;
